import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";

import { Acceso } from "./Acceso";
import { Accesibilidad } from "./Accesibilidad";
import { Actividad } from "./Actividad";
import { ErrorDeCable, quienSoy, salir, type Pantalla as PantallaDeclarada, type Yo } from "./api";
import { Avisos, useAvisos } from "./avisos";
import { Estructura } from "./Estructura";
import { Metas } from "./Metas";
import { esConstruida, type Destino, type Navegar } from "./navegacion";
import { Supuestos } from "./Supuestos";
import { Tablero } from "./Tablero";
import { Territorio } from "./Territorio";
import { Usuarios } from "./Usuarios";

/**
 * El mapa se carga SOLO cuando alguien abre su pestaña. El motor de mapas pesa
 * más que todo el resto de la app junta, y quien entra a dar de alta a tres
 * personas no tiene por qué descargarlo.
 */
const Mapa = lazy(() => import("./Mapa").then((m) => ({ default: m.Mapa })));

/** Dice si en este navegador ya hubo sesión alguna vez. Ver `recargar`. */
const MARCA_SESION = "brujula-coordinacion:hubo-sesion";

/** Los grupos del menú, por trabajo (db/045). El orden es el de la vida de
 *  una campaña: primero mi zona, luego captar, al final administrar. */
const GRUPOS: { id: PantallaDeclarada["grupo"]; titulo: string }[] = [
  { id: "mi_zona", titulo: "Mi zona" },
  { id: "captacion", titulo: "Captación" },
  { id: "administracion", titulo: "Administración" },
  { id: "otro", titulo: "Más" },
];

/** Lo que esta app llama a cada pantalla, cuando difiere del título que el
 *  servidor declara para todos los públicos. */
const TITULO_LOCAL: Record<string, string> = {
  usuarios: "Cuentas",
  eventos: "Actividad",
  metas: "Metas de captura",
};

const ACCESIBILIDAD: Destino = { pantalla: "accesibilidad" };

export function App() {
  const [yo, setYo] = useState<Yo | null>(null);
  const [cargando, setCargando] = useState(true);
  const [destino, setDestino] = useState<Destino>({ pantalla: "tablero" });
  const avisos = useAvisos();

  const navegar: Navegar = useCallback((d) => {
    setDestino({ pantalla: d.pantalla, parametros: d.parametros ?? {} });
    // Al cambiar de pantalla el foco va al contenido (WCAG 2.4.3): sin esto,
    // quien navega con teclado se queda en el botón del menú.
    requestAnimationFrame(() => document.getElementById("principal")?.focus());
  }, []);

  const recargar = useCallback(async () => {
    try {
      const quien = await quienSoy();
      // Marca que en este navegador SÍ hubo sesión. No es una credencial ni
      // sustituye a la cookie: solo evita preguntar «¿quién soy?» a quien
      // nunca entró, que es lo que dejaba un 401 en la consola de la primera
      // visita. La cookie sigue siendo la única autoridad.
      try {
        localStorage.setItem(MARCA_SESION, "1");
      } catch {
        // Navegación privada estricta: sin marca se pregunta siempre, que es
        // el comportamiento de antes y funciona igual.
      }
      setYo(quien);
      // Se abre en la PRIMERA pantalla que el servidor declare y esta app
      // sepa dibujar. Para casi todos es «Hoy»; para quien solo administra
      // cuentas, «Cuentas». El servidor decide la UI; aquí se obedece.
      const primera = quien.pantallas
        .filter((p) => esConstruida(p.id))
        .sort((a, b) => a.orden - b.orden)[0];
      setDestino({ pantalla: primera?.id ?? "accesibilidad" });
    } catch (e) {
      // 401 es el estado normal de quien todavía no entró, no un fallo: no se
      // registra en la consola para que el único ruido que quede ahí sea el
      // que importa.
      if (e instanceof ErrorDeCable && e.estado === 401) {
        try {
          localStorage.removeItem(MARCA_SESION);
        } catch {
          // ver arriba
        }
      } else {
        console.error("no pude preguntar quién soy", e);
      }
      setYo(null);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    let hubo = true;
    try {
      hubo = localStorage.getItem(MARCA_SESION) === "1";
    } catch {
      hubo = true;
    }
    if (!hubo) {
      setCargando(false);
      return;
    }
    void recargar();
  }, [recargar]);

  /** El menú: las pantallas declaradas que esta app dibuja, por grupo. */
  const menu = useMemo(() => {
    if (!yo) return [];
    const mias = yo.pantallas.filter((p) => esConstruida(p.id)).sort((a, b) => a.orden - b.orden);
    return GRUPOS.map((g) => ({ ...g, pantallas: mias.filter((p) => p.grupo === g.id) })).filter(
      (g) => g.pantallas.length > 0,
    );
  }, [yo]);

  const tituloDe = (p: PantallaDeclarada) => TITULO_LOCAL[p.id] ?? p.titulo;
  const tituloActual =
    destino.pantalla === "accesibilidad"
      ? "Accesibilidad"
      : (yo?.pantallas.filter((p) => p.id === destino.pantalla).map(tituloDe)[0] ?? "Coordinación");

  // El título de la pestaña del navegador dice en qué pantalla se está
  // (D-72, WCAG 2.4.2): con una sola página, si no se cambia aquí, no cambia.
  useEffect(() => {
    document.title = yo ? `${tituloActual} · Coordinación · Brújula` : "Entrar · Coordinación · Brújula";
  }, [tituloActual, yo]);

  if (cargando) {
    return (
      <main className="centrado">
        <p className="apagado">Cargando…</p>
      </main>
    );
  }

  if (!yo) {
    return (
      <>
        <Acceso alEntrar={recargar} />
        <Avisos mensajes={avisos.mensajes} />
      </>
    );
  }

  const noConstruidas = yo.pantallas.filter((p) => !esConstruida(p.id));
  const puede = (id: string) => yo.pantallas.some((p) => p.id === id);
  const parametros = destino.parametros ?? {};

  return (
    <div className="marco">
      {/* D-28 / WCAG 2.4.1: primer elemento enfocable de la página. */}
      <a className="saltar" href="#principal">
        Saltar al contenido
      </a>

      <header className="barra">
        <a className="marca" href="/app/coordinacion/">
          Brújula <span>Coordinación</span>
        </a>
        <nav className="menu" aria-label="Secciones">
          {menu.map((g) => (
            <div className="menu-grupo" key={g.id}>
              <span className="menu-grupo-titulo">{g.titulo}</span>
              <div className="pestanas">
                {g.pantallas.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    aria-current={destino.pantalla === p.id ? "page" : undefined}
                    onClick={() => navegar({ pantalla: p.id })}
                  >
                    {tituloDe(p)}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="quien">
          <span>
            <strong>{yo.nombre}</strong> ({yo.usuario})
          </span>
          <button
            type="button"
            className="btn terciario"
            onClick={async () => {
              await salir();
              setYo(null);
            }}
          >
            Salir
          </button>
        </div>
      </header>

      <main id="principal" tabIndex={-1}>
        {destino.pantalla === "tablero" && puede("tablero") && <Tablero yo={yo} navegar={navegar} />}
        {destino.pantalla === "territorio" && puede("territorio") && (
          <Territorio yo={yo} parametros={parametros} navegar={navegar} />
        )}
        {destino.pantalla === "mapa" && puede("mapa") && (
          <Suspense fallback={<p className="apagado panel">Cargando el mapa…</p>}>
            <Mapa yo={yo} parametros={parametros} navegar={navegar} />
          </Suspense>
        )}
        {destino.pantalla === "metas" && puede("metas") && (
          <Metas yo={yo} parametros={parametros} navegar={navegar} avisar={avisos.avisar} />
        )}
        {destino.pantalla === "supuestos" && puede("supuestos") && (
          <Supuestos yo={yo} parametros={parametros} navegar={navegar} avisar={avisos.avisar} />
        )}
        {destino.pantalla === "estructura" && puede("estructura") && (
          <Estructura yo={yo} parametros={parametros} navegar={navegar} avisar={avisos.avisar} />
        )}
        {destino.pantalla === "usuarios" && puede("usuarios") && (
          <Usuarios yo={yo} parametros={parametros} avisar={avisos.avisar} />
        )}
        {destino.pantalla === "eventos" && puede("eventos") && <Actividad yo={yo} />}
        {destino.pantalla === "accesibilidad" && <Accesibilidad />}
        {destino.pantalla !== "accesibilidad" && !puede(destino.pantalla) && (
          <section className="panel">
            <h1>Sin acceso</h1>
            <p className="ayuda">Tu sesión no alcanza esta pantalla. Pide que te la abran si la necesitas.</p>
          </section>
        )}
      </main>

      {/* D-13. No se inventan enlaces a páginas que no existen: los que
          faltan (aviso de privacidad y términos) están dichos en la
          declaración de accesibilidad con su motivo, que es más honesto que
          un enlace roto. */}
      <footer className="pie">
        <a href="/app/coordinacion/">Inicio</a>
        <button type="button" className="btn terciario" onClick={() => navegar(ACCESIBILIDAD)}>
          Accesibilidad
        </button>
        {noConstruidas.length > 0 && (
          <span className="apagado">
            Tu sesión alcanza además: {noConstruidas.map((p) => p.titulo).join(" · ")}
          </span>
        )}
        <span className="derechos">© 2026 Brújula</span>
      </footer>

      <Avisos mensajes={avisos.mensajes} />
    </div>
  );
}
